import { Component, effect, inject, input, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonButton, IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { AuthStateStore } from '@libs/core/lib/auth/auth-state.store';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import {
  withAddToWishlist,
  withRemoveFromWishlist,
} from '@libs/mtjr/shared/generated/entities/user/user-store-features';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { signalStore } from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';

export const WishlistToggleStore = signalStore(withAddToWishlist(), withRemoveFromWishlist());

@Component({
  selector: 'mtjr-heart-button',
  providers: [WishlistToggleStore],
  imports: [IonButton, IonIcon, IonSpinner],
  template: `
    <ion-button
      class="hover:scale-[110%]"
      fill="clear"
      color="danger"
      shape="round"
      (click)="$event.stopPropagation(); $event.preventDefault(); toggleAddToWishList()"
    >
      @if (store.addToWishlistLoading() || store.removeFromWishlistLoading()) {
        <ion-spinner slot="icon-only" color="danger" class="size-6" />
      } @else {
        <ion-icon slot="icon-only" [name]="addedToWishlist() ? 'heart-filled' : 'heart'" [class]="cssClass()" />
      }
    </ion-button>
  `,
})
export class HeartButtonComponent {
  cssClass = input<string>('text-danger');
  store = inject(WishlistToggleStore);
  userStore = inject(UserStore);
  authStore = inject(AuthStateStore);
  productId = input.required<string>();
  addedToWishlist = signal(false);
  eventBus = inject(NgEventBus);
  appAlerts = inject(AppAlerts);
  translate = inject(TranslateService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  constructor() {
    effect(() => {
      const wishlist = this.userStore.wishListItems();
      untracked(() => {
        this.addedToWishlist.set(wishlist.includes(this.productId()));
      });
    });

    effect(() => {
      const state = this.store.addToWishlistState();
      if (state.success) {
        this.appAlerts.successSnackbar(this.translate.instant('PRODUCTS.ADDED_TO_WISHLIST'));
        this.eventBus.cast('wishlist:updated', { productId: this.productId(), added: true });
        this.userStore.refreshLoggedInUser();
        untracked(() => {
          this.addedToWishlist.set(true);
        });
      } else if (state.type === 'error') {
        this.appAlerts.dangerSnackbar(state.failure.message);
      }
    });

    effect(() => {
      const state = this.store.removeFromWishlistState();
      if (state.success) {
        this.appAlerts.successSnackbar(this.translate.instant('PRODUCTS.REMOVED_FROM_WISHLIST'));
        this.eventBus.cast('wishlist:updated', { productId: this.productId(), added: false });
        this.userStore.refreshLoggedInUser();
        untracked(() => {
          this.addedToWishlist.set(false);
        });
      } else if (state.type === 'error') {
        this.appAlerts.dangerSnackbar(state.failure.message);
      }
    });

    effect(async () => {
      const productId = this.productId();
      if (productId) {
        const queryParams = this.route.snapshot.queryParams;
        if (queryParams['PRA_WISHLIST'] === productId) {
          await this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { PRA_WISHLIST: null },
            queryParamsHandling: 'merge',
          });
          this.store.addToWishlist({ productId: this.productId() });
        }
      }
    });
  }

  async toggleAddToWishList() {
    if (this.authStore.isAuthenticated()) {
      this.toggleInternal();
    } else {
      const triggerKey = `wishlist-toggle` + this.productId();
      this.authStore.registerAfterSignInCallback(triggerKey, () => {
        this.authStore.unregisterAfterSignInCallback(triggerKey);
        console.log('after sign in callback');
        this.toggleInternal();
      });
      this.authStore.signIn();
      // this.router.navigate([], {
      //   relativeTo: this.route,
      //   queryParams: { PRA_WISHLIST: this.productId() },
      //   queryParamsHandling: 'merge',
      // });
      // // this will trigger the AutoLoginPartialRoutesGuard to redirect to login
      // // and then back to the product page with the wishlist query param
      // // which will then trigger the addToWishlist action
      // authGuard.canLoad().subscribe();
    }
  }

  private toggleInternal() {
    if (this.addedToWishlist()) {
      this.store.removeFromWishlist({ productId: this.productId() });
    } else {
      this.store.addToWishlist({ productId: this.productId() });
    }
  }
}
